import { useRef, ChangeEvent, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import moment from "moment";
import { getBase64, isFileSupported } from "../../utils/file";

import check from "../../images/check.svg";
import trash from "../../images/trash.svg";
import camera from "../../images/camera.svg";
import sync from "../../images/sync.svg";
import pdf from "../../images/pdf.png";

type ButtonPictureProps = {
  title: string;
  value: string | undefined;
  onChange: (base64: string, formatted: string) => void;
  required?: boolean;
  isLabelOptionnal?: boolean;
};

interface ImageData {
  base64: string;
  formatted: string;
}

function ButtonPicture({
  title,
  onChange,
  value,
  required,
  isLabelOptionnal = true,
}: ButtonPictureProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [currentImage, setCurrentImage] = useState<ImageData | null>(
    value ? { base64: value, formatted: "" } : null
  );
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentImage) {
      onChange(currentImage.base64, currentImage.formatted);
    } else {
      onChange("", "");
    }
  }, [currentImage]);

  return (
    <div className="button-picture-container">
      {currentImage ? (
        <>
          <div onClick={() => setCurrentImage(null)}>
            <img className="button-picture-icon" alt="trash" src={trash} />
          </div>
          <div className="button-picture-viewer">
            <img
              height={
                currentImage.base64.includes("image/png") ||
                currentImage.base64.includes("image/jpg") ||
                currentImage.base64.includes("image/jpeg")
                  ? ""
                  : "36"
              }
              src={
                currentImage.base64.includes("image/png") ||
                currentImage.base64.includes("image/jpg") ||
                currentImage.base64.includes("image/jpeg")
                  ? currentImage.base64
                  : pdf
              }
              alt={title}
            />
          </div>
          <div onClick={() => inputRef.current?.click()}>
            <img className="button-picture-icon-right" alt="sync" src={sync} />
          </div>
        </>
      ) : null}
      <button
        className="button-picture"
        onClick={() => {
          ReactGA.event({
            category: "click_picture",
            action: "open_camera",
            value: 0,
          });
          inputRef.current?.click();
        }}
      >
        <span>
          {title}{" "}
          {required ? "(Obligatoire)" : isLabelOptionnal ? "(Facultatif)" : ""}
        </span>
        <img alt="camera" src={currentImage ? check : camera} />
      </button>
      <input
        style={{ position: "absolute", top: -1000, left: -1000 }}
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.pdf"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];

            if (!isFileSupported(file)) {
              window.umami?.track("unsupported_file_format", { type: file.type, name: file.name, component: "ButtonPicture" });
              alert("Format non supporté. Formats acceptés : JPG, PNG, GIF, WebP, HEIC, PDF.");
              return;
            }

            const time = file.lastModified
              ? moment(file.lastModified)
              : moment();

            const formatted = time.format("DD/MM/YYYY HH:mm:ss");

            getBase64(file)
              .then((result: any) => {
                ReactGA.event({
                  category: "click_picture",
                  action: "close_camera_true",
                  value: 1,
                });
                setCurrentImage({ base64: result.base64, formatted });
              })
              .catch(() => {
                ReactGA.event({
                  category: "click_picture",
                  action: "close_camera_error",
                  value: 3,
                });
              });
          } else {
            ReactGA.event({
              category: "click_picture",
              action: "close_camera_false",
              value: 2,
            });
          }
        }}
      />
    </div>
  );
}

export default ButtonPicture;
